import React, { useCallback } from 'react';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import Avatar from '../../../component/avatar';
import { AVATAR_BORDER, COMMENT_TYPE, ICON_SIZE, PRIORITY_ICON_NAMES, STATUS_TYPE } from '../../../global/constants';
import { TeamActivityCommentInterface, TeamActivityInterface } from '../../../interfaces/ReportInterface';
import { floatToHHMM, renderPriorityIconName, renderStatusIconName } from '../../../helpers/common';
import { rootStore } from '../../../mobx/rootStore';
import { format } from 'date-fns';
import {
  TeamTimeLine,
  ActivityTextDiv,
  Title,
  StatusIcon,
  ActivityContentDiv,
  ActivityItem,
  ProfileDiv
} from './styles';
import { UserModel } from '../../../mobx/models/user';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

interface ReportTeamTimeLineProps {
  title: string;
  activities: TeamActivityInterface[];
}

const ReportTeamTimeLine: React.FC<ReportTeamTimeLineProps> = ({ title, activities }) => {
  // MobX variables
  const { appStore, workspaceStore } = rootStore;
  const { themeMode } = appStore || {};
  const { currentWorkspace: workspace } = workspaceStore;

  const renderCommentText = useCallback(
    (activity: Partial<TeamActivityCommentInterface>) => {
      const assigneeDetails = workspace?.users?.find((user) => user.id === activity.Assignee);
      const activityTypeContentMap = {
        [COMMENT_TYPE.STATUS_ID_CHANGE]: (
          <span>
            change status from {activity.PreviousGroup || ''} to {activity.Group || ''}.
          </span>
        ),
        [COMMENT_TYPE.STATUS_CHANGE]: <span>{activity.Status ? 'completed task.' : 're‑opened task.'}</span>,
        [COMMENT_TYPE.UPDATE_TASK]: <span>updated task. </span>,
        [COMMENT_TYPE.UNASSIGN]: <span>removed assignee.</span>,
        [COMMENT_TYPE.REMOVE_ASSIGNEE]: <span>unassigned task to {assigneeDetails?.name || 'Unknown'}.</span>,
        [COMMENT_TYPE.ESTIMATE_TIME]: (
          <span>
            set estimate time to{' '}
            {activity?.EstimateTime !== 0 &&
              activity?.EstimateTime !== null &&
              activity?.EstimateTime !== undefined &&
              floatToHHMM(activity?.EstimateTime)}{' '}
            hours.
          </span>
        ),
        [COMMENT_TYPE.CHANGE_GROUP]: (
          <span>
            change group from {activity.PreviousGroup || ''} to {activity.Group || ''}.
          </span>
        ),
        [COMMENT_TYPE.ADD_LABEL]: <span>Added label {activity.Group || ''}.</span>,
        [COMMENT_TYPE.REMOVE_LABEL]: <span>Removed label {activity.Group || ''}.</span>,
        [COMMENT_TYPE.REMOVE_DUE_DATE]: <span>remove due date.</span>,
        [COMMENT_TYPE.CHANGE_DUE_DATE]: (
          <span>
            change due date from{' '}
            {activity.PreviousDueDate ? format(new Date(activity.PreviousDueDate), 'EEE, MMM dd, yyyy') : ''} to{' '}
            {activity.DueDate ? format(new Date(activity.DueDate), 'EEE, MMM dd, yyyy') : ''}.
          </span>
        ),
        [COMMENT_TYPE.SET_DUE_DATE]: (
          <span>
            set due date to {activity.DueDate ? format(new Date(activity.DueDate), 'EEE, MMM dd, yyyy') : ''}.
          </span>
        ),
        [COMMENT_TYPE.ADD_ASSIGNEE]: <span>assign task to {assigneeDetails?.name || 'Unknown'}.</span>,
        [COMMENT_TYPE.SET_MILESTONE]: <span>added task in milestone {activity.Group || ''}.</span>,
        [COMMENT_TYPE.CREATE_TASK]: <span>created the task.</span>,
        [COMMENT_TYPE.CHANGE_PRIORITY]: (
          <span>
            change priority from {PRIORITY_ICON_NAMES[activity.PreviousPriority || 0]}
            {' to '}
            {PRIORITY_ICON_NAMES[activity.Priority || 0]}.
          </span>
        )
      };

      return activity.Type !== undefined ? activityTypeContentMap[activity.Type] || null : null;
    },
    [workspace?.users]
  );

  // Common function to get SVG icon name based on activity type
  const getActivityIconName = useCallback((activity: Partial<TeamActivityCommentInterface>) => {
    const iconNameMap = {
      [COMMENT_TYPE.STATUS_ID_CHANGE]:
        typeof activity.Priority === 'number'
          ? renderStatusIconName(activity.Priority)
          : renderStatusIconName(STATUS_TYPE.UNSTARTED),
      [COMMENT_TYPE.CHANGE_PRIORITY]: renderPriorityIconName(activity.Priority || 0),
      [COMMENT_TYPE.SET_MILESTONE]: 'milestone-icon',
      [COMMENT_TYPE.ADD_LABEL]: 'updated-label-icon',
      [COMMENT_TYPE.REMOVE_LABEL]: 'updated-label-icon',
      [COMMENT_TYPE.ESTIMATE_TIME]: 'time-icon',
      [COMMENT_TYPE.SET_DUE_DATE]: 'calendar-icon',
      [COMMENT_TYPE.CHANGE_DUE_DATE]: 'calendar-icon',
      [COMMENT_TYPE.REMOVE_DUE_DATE]: 'calendar-icon',
      [COMMENT_TYPE.UPDATE_TASK]: 'edit-icon',
      [COMMENT_TYPE.STATUS_CHANGE]: activity.Status ? 'activity-completedtask-icon' : 'activity-reopentask-icon'
    };

    return activity.Type !== undefined
      ? iconNameMap[activity.Type] || renderStatusIconName(STATUS_TYPE.UNSTARTED)
      : renderStatusIconName(STATUS_TYPE.UNSTARTED);
  }, []);

  // Common function to get SVG icon viewBox based on activity type
  const getActivityIconViewBox = useCallback((activity: Partial<TeamActivityCommentInterface>, type: string) => {
    const smallViewBoxTypes = [
      COMMENT_TYPE.CHANGE_PRIORITY,
      COMMENT_TYPE.STATUS_CHANGE,
      COMMENT_TYPE.STATUS_ID_CHANGE,
      COMMENT_TYPE.CHANGE_GROUP,
      COMMENT_TYPE.CREATE_TASK
    ];

    const typeId = activity.Type ?? -1;
    return smallViewBoxTypes.includes(typeId) || type === 'task_created' ? '0 0 16 16' : '0 0 18 18';
  }, []);

  const renderActivity = useCallback(
    (activity: TeamActivityInterface, createdByUser: UserModel | undefined) => {
      switch (activity.type) {
        case 'task_created':
          return (
            <>
              <p>{createdByUser?.name || 'Unknown'}</p>
              <span className='text-sm text-gray-900'>created task</span>
              <p>{activity.taskName}</p>
              <span>{dayjs(activity.createdTime).fromNow()}</span>
            </>
          );
        case 'project_created':
          return (
            <>
              <p>{createdByUser?.name || 'Unknown'}</p>
              <span className='text-sm text-gray-900'>created project</span>
              <p>{activity.projectName}</p>
              <span>{dayjs(activity.createdTime).fromNow()}</span>
            </>
          );
        case 'newProject_member_added':
          const memberDetails = workspace?.users?.find((user) => user.id === activity.memberUserId);
          return (
            <>
              <p>{createdByUser?.name || 'Unknown'}</p>
              <span className='text-sm text-gray-900'>added</span>
              <p>{memberDetails?.name || 'Unknown'}</p>
              <span>into project</span>
              <p>{activity.projectName}</p>
              <span>{dayjs(activity.createdTime).fromNow()}</span>
            </>
          );
        case 'invited_user':
          return (
            <>
              <p>{createdByUser?.name || 'Unknown'}</p>
              <span className='text-sm text-gray-900'>invited user </span>
              <p>{activity.invitedUserName}</p>
              <span className='text-sm text-gray-900'>into workspace </span>
              <p>{workspace?.name}</p>
              <span>{dayjs(activity.createdTime).fromNow()}</span>
            </>
          );
        case 'task_comment':
          const commentText = renderCommentText(activity.comment || {});
          if (!commentText) {
            return null;
          }
          return (
            <>
              <p>{createdByUser?.name || 'Unknown'}</p>
              <span className='text-sm text-gray-900'>{commentText}</span>
              <p>{activity.taskName}</p>
              <span>{dayjs(activity.createdTime).fromNow()}</span>
            </>
          );
        default:
          return <></>;
      }
    },
    [renderCommentText, workspace?.name, workspace?.users]
  );

  const getOtherUserId = useCallback((activity: TeamActivityInterface) => {
    if (activity?.type === 'newProject_member_added') return activity.memberUserId;
    else if (activity?.type === 'invited_user') return activity.invitedUserId;
    else if (activity?.type === 'task_comment') return activity.comment?.Assignee;
    return null;
  }, []);

  return (
    <>
      <TeamTimeLine>
        <Title>{title}</Title>
        <ActivityContentDiv>
          {activities.map((activity) => {
            const createdByUser = workspace?.users?.find((user) => user.id === activity.createdBy);
            const assignee = workspace?.users?.find((user) => user.id === getOtherUserId(activity));
            return (
              <ActivityItem key={activity.id}>
                <ProfileDiv>
                  {activity?.createdBy === 'Github' ? (
                    <SVGIcon
                      name={themeMode?.theme === 'dark' ? 'github-inbox-dark-icon' : 'github-inbox-light-icon'}
                      width='26'
                      height='26'
                      viewBox={`0 0 ${ICON_SIZE.XXXL} ${ICON_SIZE.XXXL}`}
                      fill='var(--text-secondary)'
                    />
                  ) : (
                    <Avatar
                      imgSrc={createdByUser?.profile_photo || ''}
                      name={createdByUser?.name || 'Unknown'}
                      size={26}
                      borderVariant={AVATAR_BORDER.NONE}
                    />
                  )}
                  <StatusIcon>
                    {activity.comment?.Type === COMMENT_TYPE.REMOVE_ASSIGNEE ||
                    activity.comment?.Type === COMMENT_TYPE.UNASSIGN ||
                    activity.comment?.Type === COMMENT_TYPE.ADD_ASSIGNEE ||
                    ['newProject_member_added', 'invited_user'].includes(activity.type) ? (
                      <Avatar
                        imgSrc={assignee?.profile_photo || ''}
                        name={assignee?.name || activity?.invitedUserName || 'Unknown'}
                        size={26}
                        borderVariant={AVATAR_BORDER.NONE}
                      />
                    ) : (
                      <SVGIcon
                        name={getActivityIconName(activity.comment || {})}
                        width={ICON_SIZE.MD}
                        height={ICON_SIZE.MD}
                        viewBox={getActivityIconViewBox(activity.comment || {}, activity.type)}
                        fill='var(--text-secondary)'
                      />
                    )}
                  </StatusIcon>
                </ProfileDiv>
                <ActivityTextDiv>{renderActivity(activity, createdByUser)}</ActivityTextDiv>
              </ActivityItem>
            );
          })}
        </ActivityContentDiv>
      </TeamTimeLine>
    </>
  );
};

export default ReportTeamTimeLine;
